import React, { ComponentProps, FormEvent, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'hooks/useRouter'
import { useToast } from 'contexts/ToastContext'
import { useCreatePassword } from 'features/Login/api/mutations/useCreatePassword'
import { Layout } from 'components/Layout'
import { Header } from 'components/Header'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { LoginLayout } from '../LoginLayout'
import { Icon } from 'components/Icon'
import { CreatePasswordRoot, TogglePasswordVisibilityButton } from './CreatePassword.styles'
import { useAuth } from 'contexts/AuthContext'

type CreatePasswordOwnProps = ComponentProps<typeof CreatePasswordRoot>
export type CreatePasswordProps = CreatePasswordOwnProps & {}

export const CreatePassword = (_props: CreatePasswordProps) => {
  const { t } = useTranslation()

  const router = useRouter()
  const { token } = router.query as { token: string }

  const { addToast, removeToast } = useToast()

  const { createPassword } = useCreatePassword(token)
  const { confirmed } = useAuth()

  const [isPasswordHidden, setIsPasswordHidden] = useState(true)
  const [password, setPassword] = useState('')
  const [confirmedPassword, setConfirmedPassword] = useState('')
  const arePasswordsMatching = password === confirmedPassword
  const [errors, setErrors] = useState({
    password: false,
    'confirmed-password': false,
  })

  const header = <Header />

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (password.length < 8) {
      setErrors({ ...errors, password: true })
    }

    if (!arePasswordsMatching) {
      setErrors({ ...errors, 'confirmed-password': true })
    }

    createPassword(
      { password },
      {
        onSuccess: async () => {
          const isLoggedIn = confirmed
          await router.push('/login')
          addToast({
            title: t(
              `login:toast.password.${isLoggedIn ? 'authenticated' : 'guest'}.success.title`
            ),
            message: t(
              `login:toast.password.${isLoggedIn ? 'authenticated' : 'guest'}.success.message`
            ),
            duration: 5000,
            onClose: () => removeToast('success-password'),
            tone: 'success',
            id: `success-password`,
          })
        },
        onError: () => {
          addToast({
            title: t('login:toast.password.error.title'),
            message: t('login:toast.password.error.message'),
            duration: 5000,
            onClose: () => removeToast('error-password'),
            tone: 'error',
            id: 'error-password',
          })
        },
      }
    )
  }

  return (
    <Layout header={header} hideCTA>
      <LoginLayout skipOnboarding>
        <CreatePasswordRoot onSubmit={handleSubmit}>
          <Stack direction="column" gap="7">
            <Stack direction="column" gap={{ '@initial': '4', '@bp3': '7' }}>
              <Text
                size={{ '@initial': 'h4', '@bp3': 'h3' }}
                as="h3"
                weight="bold"
                color="accent-black"
              >
                {t('create-password:title')}
              </Text>
              <Stack direction="column" gap={{ '@initial': '3', '@bp3': '4' }}>
                <Text size={{ '@initial': 'body', '@bp3': 'headline' }} color="accent-black">
                  {t('create-password:description')}
                </Text>
                <Stack direction="column" gap={{ '@initial': '5', '@bp3': '4' }}>
                  <TextInput
                    name="password"
                    error={errors['password']}
                    label={t('create-password:password')}
                    type={isPasswordHidden ? 'password' : 'text'}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText={
                      password.length >= 8 || password === ''
                        ? ''
                        : t('create-password:required-length')
                    }
                    suffix={
                      <TogglePasswordVisibilityButton
                        type="button"
                        aria-label={
                          isPasswordHidden ? t('create-password:show') : t('create-password:hide')
                        }
                        onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                      >
                        <Icon
                          name={isPasswordHidden ? 'eye-closed' : 'eye-open'}
                          color="tertiary-200"
                        />
                      </TogglePasswordVisibilityButton>
                    }
                  />
                  <TextInput
                    name="confirmed-password"
                    error={errors['confirmed-password']}
                    label={t('create-password:confirm')}
                    type={isPasswordHidden ? 'password' : 'text'}
                    onChange={(e) => setConfirmedPassword(e.target.value)}
                    helperText={
                      arePasswordsMatching || confirmedPassword === ''
                        ? ''
                        : t('create-password:match')
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
            <Button>{t('create-password:reset-password')}</Button>
          </Stack>
        </CreatePasswordRoot>
      </LoginLayout>
    </Layout>
  )
}

CreatePassword.displayName = 'CreatePassword'
